import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { callApi } from "../callEndpoint";
import {
  customerEndpoints,
  type CustomerProfile,
  type PatientAppointmentsRequest,
  type PatientAppointmentRecord,
  type PatientAppointmentDetail,
  type UpdateCustomerProfileRequest,
  type PaymentMethodSummary,
  type SetupIntentResponse,
} from "../contracts/customer.contract";

export const paymentMethodQueryKey = ["customer-payment-method"] as const;

export const useCustomerProfile = (options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: ["customer-profile"],
    queryFn: () => callApi<CustomerProfile>(customerEndpoints.profile),
    enabled: options?.enabled,
  });

export const useUpdateCustomerProfile = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateCustomerProfileRequest) =>
      callApi<CustomerProfile>(customerEndpoints.updateProfile, {
        data: payload,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["customer-profile"] }),
  });
};

export const useCustomerAppointments = (
  params?: PatientAppointmentsRequest,
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: ["customer-appointments", params],
    queryFn: async () => {
      const data = await callApi<PatientAppointmentRecord[]>(
        customerEndpoints.appointments,
        { params },
      );
      return data ?? [];
    },
    enabled: options?.enabled,
  });

/**
 * Never cached, unlike the rest of the app's queries.
 *
 * `cancellationPolicy` on this response is evaluated against the server clock
 * at request time, so it is only true for that response. Held for the global
 * 60s staleTime it can tell a patient cancelling is free seconds after the
 * notice window closed — the cancel itself would still be charged correctly,
 * but the copy they committed on would have lied to them.
 */
export const useCustomerAppointmentById = (id: string) =>
  useQuery({
    queryKey: ["customer-appointment", id],
    queryFn: () =>
      callApi<PatientAppointmentDetail>(customerEndpoints.appointmentById(id)),
    enabled: !!id,
    staleTime: 0,
  });

export const useCustomerById = (id: string) =>
  useQuery({
    queryKey: ["customer-info", id],
    queryFn: () => callApi<CustomerProfile>(customerEndpoints.getById(id)),
    enabled: !!id,
  });

export const useCustomerPaymentMethod = (options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: paymentMethodQueryKey,
    queryFn: () =>
      callApi<PaymentMethodSummary>(customerEndpoints.paymentMethod),
    enabled: options?.enabled,
  });

// Creates a save-only SetupIntent and returns its client secret for Stripe
// Elements. Not cached — each card-entry session needs a fresh intent.
export const useCreateSetupIntent = () =>
  useMutation({
    mutationFn: () =>
      callApi<SetupIntentResponse>(customerEndpoints.createSetupIntent),
  });

export const useCancelCustomerAppointment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (appointmentId: string) =>
      callApi(customerEndpoints.cancelAppointment, {
        data: { appointmentId },
      }),
    onSuccess: (_, appointmentId) => {
      qc.invalidateQueries({ queryKey: ["customer-appointments"] });
      qc.invalidateQueries({
        queryKey: ["customer-appointment", appointmentId],
      });
    },
  });
};
